import type { SessionAuthMethod } from "@repo/app-types/api/user";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconCloudLockOpen, IconMail } from "@tabler/icons-react";
import type { FC } from "react";
import {
  GoogleSignInButton,
  MicrosoftSignInButton,
} from "./oauthProviderButtons.js";

export interface VisibleAuthMethods {
  showOAuth: boolean;
  showSso: boolean;
  showEmail: boolean;
}

/**
 * Maps a company's accepted auth methods (from `acceptedAuthMethodsForCompany`
 * on the server) to which sign-in controls to render. SSO is offered only when
 * the company actually federates with a provider — a plain company that merely
 * "accepts" SSO sessions in policy has no provider to launch, so we don't show a
 * dead-end SSO button. Shared by the invitation sign-in screen and the
 * re-authentication (auth-method mismatch) screen so both stay in lockstep.
 */
export function visibleAuthMethods({
  acceptedMethods,
  ssoProviderIds,
}: {
  acceptedMethods: readonly SessionAuthMethod[];
  ssoProviderIds: readonly string[];
}): VisibleAuthMethods {
  return {
    showOAuth: acceptedMethods.includes("oauth"),
    showSso: ssoProviderIds.length > 0,
    showEmail: acceptedMethods.includes("magic_link"),
  };
}

/** One-click SSO button that launches the company's configured provider. */
export const SsoSignInButton: FC<{ label?: string; onClick: () => void }> = ({
  label = "Sign in with SSO",
  onClick,
}) => (
  <Button className="w-52" onClick={onClick} variant={ButtonVariant.Primary}>
    <div className="grid grid-cols-[auto_1fr] justify-center gap-2">
      <IconCloudLockOpen className="size-5 shrink-0" />
      <span>{label}</span>
    </div>
  </Button>
);

/** Secondary button that switches to the email magic-link entry form. */
export const EmailSignInButton: FC<{
  isSignUp?: boolean;
  onClick: () => void;
}> = ({ isSignUp = false, onClick }) => (
  <Button className="w-52" onClick={onClick} variant={ButtonVariant.Secondary}>
    <div className="grid grid-cols-[auto_1fr] justify-center gap-2">
      <IconMail className="size-5 shrink-0" />
      <span>{isSignUp ? "Sign up with email" : "Sign in with email"}</span>
    </div>
  </Button>
);

interface AuthMethodOptionsProps {
  acceptedMethods: readonly SessionAuthMethod[];
  ssoProviderIds: readonly string[];
  /** Use "Sign up with…" labels (invitation flow) instead of "Sign in with…". */
  isSignUp?: boolean;
  onGoogleSignIn: () => void;
  onMicrosoftSignIn: () => void;
  onSsoSignIn: () => void;
  /**
   * Switches to the email magic-link form. When omitted (e.g. the
   * re-authentication mismatch screen, which never surfaces email) no email
   * button is shown even if the company accepts magic link.
   */
  onEmailSignIn?: () => void;
}

/**
 * Renders only the sign-in methods a company accepts, in a vertical stack.
 * Presentational — the actual auth side effects are supplied by the container
 * via the handler props.
 */
export const AuthMethodOptions: FC<AuthMethodOptionsProps> = ({
  acceptedMethods,
  ssoProviderIds,
  isSignUp = false,
  onGoogleSignIn,
  onMicrosoftSignIn,
  onSsoSignIn,
  onEmailSignIn,
}) => {
  const { showOAuth, showSso, showEmail } = visibleAuthMethods({
    acceptedMethods,
    ssoProviderIds,
  });

  return (
    <>
      {showOAuth ? (
        <>
          <GoogleSignInButton
            label={isSignUp ? "Sign up with Google" : "Sign in with Google"}
            onClick={onGoogleSignIn}
          />
          <MicrosoftSignInButton
            label={
              isSignUp ? "Sign up with Microsoft" : "Sign in with Microsoft"
            }
            onClick={onMicrosoftSignIn}
          />
        </>
      ) : null}
      {showSso ? <SsoSignInButton onClick={onSsoSignIn} /> : null}
      {showEmail && onEmailSignIn ? (
        <EmailSignInButton isSignUp={isSignUp} onClick={onEmailSignIn} />
      ) : null}
    </>
  );
};
